import { useCallback, useState } from 'react';
import type { KeyboardEvent, ComponentPropsWithoutRef } from 'react';
import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';
import loadable from '@loadable/component';

import type { CustomRenderer } from '@glideapps/glide-data-grid';

import type { CustomCellEditor, CustomCellProps as Props } from '../types';

import { formatterCurrency } from '@/utilities/format';

import { DECIMAL_SCALE } from '../constants';

import { isCustomCellOfKind, drawCellError } from '../utils';

import type { SharePriceCell } from './types';

import { kind } from './types';
import { validatePastedValue, validateSharePrice } from './utils';

const NumericInput = loadable(
  () => import('../../../../../../../components/NumericField'),
  {
    resolveComponent: (c) => c.NumericInput,
  }
);

type NumberFormatValues = Parameters<
  NonNullable<ComponentPropsWithoutRef<typeof NumericInput>['onValueChange']>
>[0];

const sanitisedInitialValue = (initialValue: string | undefined) => {
  const value = Number(initialValue);
  if (isNaN(value)) return null;
  return value;
};

const Editor: CustomCellEditor<SharePriceCell> = ({
  value: cell,
  initialValue,
  onFinishedEditing,
}: Props<SharePriceCell>) => {
  const [state, setState] = useState<number | null>(
    sanitisedInitialValue(initialValue) ?? cell.data.value ?? null
  );

  const handleChange = useCallback((value: NumberFormatValues) => {
    const { floatValue } = value;
    const validatedValue =
      typeof floatValue === 'undefined'
        ? null
        : Number.isNaN(floatValue ?? 0)
          ? 0
          : floatValue;

    setState(validatedValue);
  }, []);

  const handleBlur = useCallback(() => {
    const error = validateSharePrice(state);
    onFinishedEditing(
      {
        ...cell,
        data: {
          ...cell.data,
          value: error === null ? state : null,
          error,
        },
      },
      [1, 0]
    );
  }, [onFinishedEditing, state, cell]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleBlur();
    },
    [handleBlur]
  );

  return (
    <div className="grid h-[53px] items-center px-x0_75">
      <NumericInput
        aria-label="Share Price"
        autoFocus={true}
        decimalScale={DECIMAL_SCALE}
        value={state}
        onValueChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export const Render: CustomRenderer<SharePriceCell> = {
  kind: GridCellKind.Custom,
  isMatch: isCustomCellOfKind(kind),
  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, currencyISO, error } = cell.data;

    const isError = error !== null;

    if (isError) {
      drawCellError({ ctx, theme, rect, error });
    } else {
      const text =
        value && currencyISO
          ? formatterCurrency(Number(value), currencyISO)
          : String(value ?? '');
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        text,
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    }
    return true;
  },
  measure: (ctx, cell, theme) => {
    const { value } = cell.data;
    return (
      (value ? ctx.measureText(String(value ?? '')).width : 0) +
      theme.cellHorizontalPadding * 2
    );
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onPaste: validatePastedValue,
};
